import type { LucideIcon } from "lucide-react";
import { ArrowRight, Check, ChevronRight, CircleAlert, Search } from "lucide-react";
import { Link } from "react-router";
import { useAuth } from "~/lib/hooks/use-auth";

type SegmentCard = {
	icon: LucideIcon;
	title: string;
	description: string;
};

type PreviewItem = {
	icon: LucideIcon;
	label: string;
	value: string;
};

export type SegmentLandingConfig = {
	eyebrow: string;
	titleLead: string;
	titleHighlight: string;
	description: string;
	heroNote: string;
	proofPoints: string[];
	preview: {
		overline: string;
		title: string;
		subtitle: string;
		badge: string;
		items: PreviewItem[];
	};
	valueOverline: string;
	valueHeading: string;
	valueDescription: string;
	valueCards: SegmentCard[];
	workflowHeading: string;
	workflowDescription: string;
	workflowSteps: SegmentCard[];
	depthOverline: string;
	depthHeading: string;
	depthDescription: string;
	depthCards: SegmentCard[];
	legalHeading: string;
	legalDescription: string;
	legalNote: string;
	trialHeading: string;
	trialDescription: string;
};

const segmentLinks = [
	{
		label: "Účetní a daňové kanceláře",
		href: "/pro-ucetni-a-danove-poradce",
	},
	{
		label: "Finanční a nákupní týmy",
		href: "/pro-financni-tymy",
	},
	{
		label: "Právní a insolvenční praxe",
		href: "/pro-pravniky-a-insolvencni-spravce",
	},
];

function TrialLink({ children, className }: { children: React.ReactNode; className: string }) {
	const { user, loading } = useAuth();

	if (loading) {
		return <span className={`${className} opacity-70`}>{children}</span>;
	}

	return (
		<Link to={user ? "/dashboard" : "/login?mode=register"} className={className}>
			{user ? "Přejít na dashboard" : children}
		</Link>
	);
}

function SegmentPreview({ preview }: { preview: SegmentLandingConfig["preview"] }) {
	return (
		<div className="relative mx-auto w-full max-w-xl lg:mx-0">
			<div className="pointer-events-none absolute -left-8 top-10 h-44 w-44 rounded-full bg-primary-300/30 blur-3xl" />
			<div className="pointer-events-none absolute -right-8 bottom-6 h-52 w-52 rounded-full bg-accent-300/25 blur-3xl" />

			<div className="glass-panel-strong relative rounded-3xl p-3 sm:p-4">
				<div className="rounded-2xl bg-white/75 p-5 sm:p-6">
					<div className="flex items-start justify-between gap-4">
						<div>
							<p className="label-overline text-primary-700">{preview.overline}</p>
							<h2 className="mt-2 text-xl font-semibold text-text">{preview.title}</h2>
							<p className="mt-1 text-xs leading-relaxed text-muted">{preview.subtitle}</p>
						</div>
						<span className="shrink-0 rounded-full bg-secondary-100 px-3 py-1 text-xs font-semibold text-secondary-700">
							{preview.badge}
						</span>
					</div>

					<div className="mt-6 grid gap-3">
						{preview.items.map((item, index) => (
							<div
								key={item.label}
								className="card-tonal flex items-center gap-3 rounded-xl px-4 py-3.5"
							>
								<span
									className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl ${
										index === 0
											? "bg-primary-50 text-primary-700"
											: index === 1
												? "bg-secondary-50 text-secondary-700"
												: "bg-accent-50 text-accent-700"
									}`}
								>
									<item.icon className="h-4 w-4" />
								</span>
								<div className="min-w-0 flex-1">
									<p className="text-xs font-medium text-muted">{item.label}</p>
									<p className="mt-0.5 text-sm font-semibold text-text">{item.value}</p>
								</div>
								<ChevronRight className="h-4 w-4 shrink-0 text-gray-400" />
							</div>
						))}
					</div>

					<div className="mt-4 flex items-center gap-2 rounded-xl bg-primary-50/80 px-4 py-3 text-xs font-medium text-primary-800">
						<Check className="h-4 w-4 shrink-0" />
						Ilustrační ukázka dostupných podkladů
					</div>
				</div>
			</div>
		</div>
	);
}

function HeroSection({ config }: { config: SegmentLandingConfig }) {
	return (
		<section className="public-hero relative mx-3 mt-3 overflow-hidden rounded-3xl px-5 py-14 sm:mx-4 sm:px-8 lg:px-10 lg:py-20">
			<div className="mx-auto grid max-w-7xl gap-14 lg:grid-cols-[1.02fr_0.98fr] lg:items-center lg:gap-16">
				<div className="relative z-10 max-w-2xl">
					<div className="inline-flex items-center gap-2 rounded-full bg-white/75 px-3.5 py-2 text-xs font-semibold text-primary-700 shadow-sm">
						<span className="h-2 w-2 rounded-full bg-secondary-500" />
						{config.eyebrow}
					</div>
					<h1
						aria-label={`${config.titleLead} ${config.titleHighlight}`}
						className="mt-6 text-4xl font-extrabold leading-[1.05] tracking-tight text-text sm:text-5xl lg:text-6xl"
					>
						{config.titleLead} <span className="text-primary-600">{config.titleHighlight}</span>
					</h1>
					<p className="mt-6 max-w-xl text-lg leading-relaxed text-muted lg:text-xl">
						{config.description}
					</p>
					<p className="mt-3 max-w-xl text-xs leading-relaxed text-muted">{config.heroNote}</p>

					<div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
						<TrialLink className="inline-flex h-12 items-center justify-center gap-2 rounded-full bg-primary-500 px-6 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-primary-600">
							Vyzkoušet zdarma
							<ArrowRight className="h-4 w-4" />
						</TrialLink>
						<Link
							to="/search"
							className="inline-flex h-12 items-center justify-center gap-2 rounded-full bg-white/70 px-6 text-sm font-semibold text-text shadow-sm transition-colors hover:bg-white"
						>
							<Search className="h-4 w-4" />
							Prověřit firmu
						</Link>
					</div>

					<div className="mt-5 flex flex-wrap gap-x-5 gap-y-2 text-xs font-medium text-muted">
						{config.proofPoints.map((point) => (
							<span key={point} className="flex items-center gap-1.5">
								<Check className="h-3.5 w-3.5 text-secondary-600" />
								{point}
							</span>
						))}
					</div>
				</div>

				<SegmentPreview preview={config.preview} />
			</div>
		</section>
	);
}

function ValueSection({ config }: { config: SegmentLandingConfig }) {
	return (
		<section id="prinos" className="px-5 py-20 sm:px-8 lg:px-10 lg:py-28">
			<div className="mx-auto max-w-7xl">
				<div className="max-w-3xl">
					<p className="label-overline text-primary-700">{config.valueOverline}</p>
					<h2 className="mt-3 text-3xl font-bold tracking-tight text-text sm:text-4xl">
						{config.valueHeading}
					</h2>
					<p className="mt-4 max-w-2xl text-base leading-relaxed text-muted">
						{config.valueDescription}
					</p>
				</div>

				<div className="mt-12 grid gap-5 lg:grid-cols-3">
					{config.valueCards.map((card, index) => (
						<article key={card.title} className="card-surface rounded-2xl p-6 sm:p-7">
							<span
								className={`flex h-11 w-11 items-center justify-center rounded-xl ${
									index === 0
										? "bg-primary-50 text-primary-700"
										: index === 1
											? "bg-secondary-50 text-secondary-700"
											: "bg-accent-50 text-accent-700"
								}`}
							>
								<card.icon className="h-5 w-5" />
							</span>
							<h3 className="mt-6 text-lg font-semibold text-text">{card.title}</h3>
							<p className="mt-3 text-sm leading-relaxed text-muted">{card.description}</p>
						</article>
					))}
				</div>
			</div>
		</section>
	);
}

function WorkflowSection({ config }: { config: SegmentLandingConfig }) {
	return (
		<section className="px-3 py-4 sm:px-4">
			<div className="glass-panel-strong mx-auto max-w-7xl rounded-3xl px-5 py-14 sm:px-8 lg:px-10 lg:py-20">
				<div className="grid gap-10 lg:grid-cols-[0.78fr_1.22fr] lg:gap-16">
					<div className="max-w-lg">
						<p className="label-overline text-primary-700">Jak zapadne do vaší práce</p>
						<h2 className="mt-3 text-3xl font-bold tracking-tight text-text sm:text-4xl">
							{config.workflowHeading}
						</h2>
						<p className="mt-4 text-base leading-relaxed text-muted">
							{config.workflowDescription}
						</p>
					</div>

					<div className="grid gap-4">
						{config.workflowSteps.map((step, index) => (
							<div
								key={step.title}
								className="card-surface grid gap-4 rounded-2xl p-5 sm:grid-cols-[auto_1fr_auto] sm:items-center sm:p-6"
							>
								<span className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary-50 text-primary-700">
									<step.icon className="h-5 w-5" />
								</span>
								<div>
									<h3 className="text-base font-semibold text-text">{step.title}</h3>
									<p className="mt-1 text-sm leading-relaxed text-muted">{step.description}</p>
								</div>
								<span className="font-mono text-xs font-semibold text-gray-400">
									{String(index + 1).padStart(2, "0")}
								</span>
							</div>
						))}
					</div>
				</div>
			</div>
		</section>
	);
}

function DepthSection({ config }: { config: SegmentLandingConfig }) {
	return (
		<section className="px-5 py-20 sm:px-8 lg:px-10 lg:py-28">
			<div className="mx-auto max-w-7xl">
				<div className="mx-auto max-w-3xl text-center">
					<p className="label-overline text-primary-700">{config.depthOverline}</p>
					<h2 className="mt-3 text-3xl font-bold tracking-tight text-text sm:text-4xl">
						{config.depthHeading}
					</h2>
					<p className="mt-4 text-base leading-relaxed text-muted">{config.depthDescription}</p>
				</div>

				<div className="mt-12 grid gap-5 lg:grid-cols-3">
					{config.depthCards.map((card) => (
						<article key={card.title} className="card-surface flex gap-4 rounded-2xl p-6 sm:p-7">
							<span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-accent-50 text-accent-700">
								<card.icon className="h-5 w-5" />
							</span>
							<div>
								<h3 className="text-base font-semibold text-text">{card.title}</h3>
								<p className="mt-2 text-sm leading-relaxed text-muted">{card.description}</p>
							</div>
						</article>
					))}
				</div>
			</div>
		</section>
	);
}

function LegalSection({ config }: { config: SegmentLandingConfig }) {
	return (
		<section className="px-3 py-4 sm:px-4">
			<div className="mx-auto grid max-w-7xl gap-10 overflow-hidden rounded-3xl bg-gray-900 px-5 py-14 text-white shadow-card sm:px-8 lg:grid-cols-[1.08fr_0.92fr] lg:items-center lg:px-10 lg:py-20">
				<div className="max-w-2xl">
					<p className="label-overline text-primary-300">Férově o možnostech</p>
					<h2 className="mt-3 text-3xl font-bold tracking-tight text-white sm:text-4xl">
						{config.legalHeading}
					</h2>
					<p className="mt-5 text-base leading-relaxed text-gray-300">{config.legalDescription}</p>
					<div className="mt-6 flex items-start gap-3 rounded-2xl bg-white/10 p-4">
						<CircleAlert className="mt-0.5 h-5 w-5 shrink-0 text-primary-300" />
						<p className="text-sm leading-relaxed text-gray-200">{config.legalNote}</p>
					</div>
				</div>

				<div className="grid gap-3 sm:grid-cols-2">
					{[
						"Veřejné zdrojové registry",
						"Datum synchronizace",
						"Vazba na původní listinu",
						"Možnost nahlásit chybu",
					].map((item) => (
						<div key={item} className="flex items-center gap-3 rounded-2xl bg-white/10 px-4 py-4">
							<span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary-500 text-white">
								<Check className="h-4 w-4" />
							</span>
							<span className="text-sm font-semibold text-white">{item}</span>
						</div>
					))}
				</div>
			</div>
		</section>
	);
}

function OtherSegments() {
	return (
		<section className="px-5 py-20 sm:px-8 lg:px-10">
			<div className="mx-auto max-w-7xl">
				<p className="label-overline text-primary-700">FirmIndex podle způsobu práce</p>
				<div className="mt-3 flex flex-col justify-between gap-5 lg:flex-row lg:items-end">
					<h2 className="max-w-2xl text-3xl font-bold tracking-tight text-text sm:text-4xl">
						Pracujete s firmami jinak? Vyberte si svůj scénář.
					</h2>
					<Link
						to="/"
						className="inline-flex items-center gap-1 text-sm font-semibold text-primary-700 transition-colors hover:text-primary-800"
					>
						Celý přehled FirmIndexu
						<ArrowRight className="h-4 w-4" />
					</Link>
				</div>

				<div className="mt-8 grid gap-4 lg:grid-cols-3">
					{segmentLinks.map((segment) => (
						<Link
							key={segment.href}
							to={segment.href}
							className="card-surface flex items-center justify-between gap-4 rounded-2xl p-5 transition-shadow hover:shadow-md"
						>
							<span className="text-sm font-semibold text-text">{segment.label}</span>
							<ChevronRight className="h-4 w-4 shrink-0 text-primary-600" />
						</Link>
					))}
				</div>
			</div>
		</section>
	);
}

function TrialSection({ config }: { config: SegmentLandingConfig }) {
	return (
		<section className="px-3 pb-3 sm:px-4">
			<div className="bg-gradient-brand mx-auto max-w-7xl overflow-hidden rounded-3xl px-5 py-14 text-white shadow-glow sm:px-8 lg:px-10 lg:py-20">
				<div className="grid gap-10 lg:grid-cols-[1fr_auto] lg:items-center lg:gap-16">
					<div className="max-w-3xl">
						<p className="label-overline text-white/75">Vyzkoušení zdarma</p>
						<h2 className="mt-3 text-3xl font-bold tracking-tight text-white sm:text-4xl lg:text-5xl">
							{config.trialHeading}
						</h2>
						<p className="mt-5 max-w-2xl text-base leading-relaxed text-white/80 lg:text-lg">
							{config.trialDescription}
						</p>
						<div className="mt-6 flex flex-wrap gap-x-5 gap-y-2 text-sm font-medium text-white/90">
							<span className="flex items-center gap-1.5">
								<Check className="h-4 w-4" /> 14 dní Premium
							</span>
							<span className="flex items-center gap-1.5">
								<Check className="h-4 w-4" /> Bez platební karty
							</span>
							<span className="flex items-center gap-1.5">
								<Check className="h-4 w-4" /> Potom plán Zdarma
							</span>
						</div>
					</div>

					<TrialLink className="inline-flex h-12 items-center justify-center gap-2 rounded-full bg-white px-7 text-sm font-semibold text-primary-700 shadow-sm transition-colors hover:bg-primary-50">
						Vytvořit účet zdarma
						<ArrowRight className="h-4 w-4" />
					</TrialLink>
				</div>
			</div>
		</section>
	);
}

export function SegmentLandingPage({ config }: { config: SegmentLandingConfig }) {
	return (
		<>
			<HeroSection config={config} />
			<ValueSection config={config} />
			<WorkflowSection config={config} />
			<DepthSection config={config} />
			<LegalSection config={config} />
			<OtherSegments />
			<TrialSection config={config} />
		</>
	);
}
